// components/ora/Chat/Companion/chatText.ts
//
// Text helpers shared by the AI-companion thread.

import { watchTokensToText } from '../shared/watchToken';

/**
 * One inline reference the old synthesis prompt could write: a content ref
 * (`A1`, `Q2:4`), a document-page ref (`p4`), or `X` for "no source".
 */
const LEGACY_CITATION_REF = '(?:[AQFVDU]\\d+(?::\\d+)?|p\\d+|X)';

/**
 * A bracketed list of those refs: `[A1]`, `[p4]`, `[X]`, and — the case the
 * first pass missed (ORA-459) — any comma- or space-separated mix of them,
 * `[A1, A2]`, `[p2, p3]`, `[A2, p3]`. The new protocol carries structured
 * citations on `text.done` instead, so nothing can resolve any of these.
 *
 * Two things deliberately survive. A bracket whose content is not a ref
 * (`[1]`, `[see below]`) is the student's or the model's own prose, and a
 * bracket followed by `(` is a markdown link's label — dropping either would
 * damage the message rather than clean it up.
 *
 * Mirrors the marker half of `INLINE_MARKDOWN_PATTERN` in the ported markdown
 * renderer, which is what produced the clickable chips in the legacy panel.
 */
const LEGACY_CITATION_MARKER_RE = new RegExp(
  `\\[${LEGACY_CITATION_REF}(?:[\\s,]+${LEGACY_CITATION_REF})*\\](?!\\()`,
  'g'
);

/**
 * Strip the legacy inline markers out of an old assistant message so it reads
 * as clean prose. Legacy conversations are never migrated (spec), so this runs
 * at render time on hydrated history only.
 *
 * Removing a marker leaves whitespace behind — a dangling space before the
 * sentence's full stop, or a double space mid-sentence — so the gap is closed
 * as well. Line structure is preserved; only horizontal whitespace is touched.
 */
export const stripLegacyCitationMarkers = (text: string): string =>
  text
    .replace(LEGACY_CITATION_MARKER_RE, '')
    .replace(/[ \t]+([.,;:!?)\]])/g, '$1')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/[ \t]+$/gm, '');

/**
 * Named entities worth decoding without a DOM. The browser's own table is used
 * when there is a document (it covers everything, including the Greek letters
 * the old model emitted); this list is the fallback for a bare environment.
 */
const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
};

const decodeWithoutDom = (value: string): string =>
  value.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (match, body: string) => {
    if (body.startsWith('#')) {
      const isHex = body[1] === 'x' || body[1] === 'X';

      const code = Number.parseInt(
        isHex ? body.slice(2) : body.slice(1),
        isHex ? 16 : 10
      );

      return Number.isFinite(code) && code > 0
        ? String.fromCodePoint(code)
        : match;
    }

    return NAMED_ENTITIES[body.toLowerCase()] ?? match;
  });

/**
 * Turn HTML entities into the characters they stand for. A `<textarea>`'s
 * content is RCDATA, so assigning `innerHTML` decodes entities without parsing
 * or executing any markup — the same trick the ported renderer uses.
 */
export const decodeHtmlEntities = (value: string): string => {
  if (!value.includes('&')) return value;
  if (typeof document === 'undefined') return decodeWithoutDom(value);

  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
};

/**
 * Block-level tags that mark a message as V1-era HTML rather than the markdown
 * every later message was stored as.
 */
const HTML_BODY_RE =
  /<\/?(p|div|ul|ol|li|table|tr|td|th|h[1-6]|blockquote|pre|section|article)\b[^>]*>/i;

/** True when a legacy message body is HTML, not markdown. */
export const looksLikeLegacyHtml = (text: string): boolean =>
  HTML_BODY_RE.test(text);

/**
 * Read a legacy HTML message as text. Old V1 conversations were persisted as
 * HTML; nothing in the new thread renders markup (no `dangerouslySetInnerHTML`
 * anywhere in this feature), so the tags are turned into line structure and
 * dropped, and the entities they carried are decoded.
 */
export const htmlToPlainText = (html: string): string =>
  decodeHtmlEntities(
    html
      .replace(/<\s*(script|style)\b[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, '')
      .replace(/<\s*br\s*\/?\s*>/gi, '\n')
      .replace(/<\s*\/\s*(p|div|li|tr|h[1-6]|blockquote|pre)\s*>/gi, '\n')
      .replace(/<\s*li\b[^>]*>/gi, '• ')
      .replace(/<[^>]*>/g, '')
  )
    .replace(/[ \t]+/g, ' ')
    .replace(/[ \t]+$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

/** A fenced code block, with or without a language tag. */
const FENCED_CODE_RE = /```[\s\S]*?```/g;
/**
 * A table's delimiter row (`| --- | :-- |`): every character is a pipe, a
 * dash, a colon or space, and at least one of them is a pipe.
 */
const TABLE_DELIMITER_RE = /^[ \t]{0,3}\|?[ \t:|-]*\|[ \t:|-]*\|?[ \t]*$/;
/**
 * A table row, recognised by the leading pipe alone. Deliberately strict: a
 * pipe inside prose ("P(A|B)", "sensitivity|specificity") is a character the
 * student wrote, and turning it into a cell break would rewrite the sentence.
 * A pipe table written without leading pipes therefore keeps its pipes; that
 * is the safer half of the trade.
 */
const TABLE_ROW_RE = /^[ \t]{0,3}\|/;

/**
 * Read the tables in an answer as their cells: the delimiter row drops out
 * entirely and each row's cells are separated by a pause. Line by line, so no
 * replacement can run two rows into one sentence.
 */
const tablesToSentences = (text: string): string =>
  text
    .split('\n')
    .filter((line) => !TABLE_DELIMITER_RE.test(line))
    .map((line) =>
      TABLE_ROW_RE.test(line)
        ? line
            .replace(/^[ \t]*\|/, '')
            .replace(/\|[ \t]*$/, '')
            .replace(/[ \t]*\|[ \t]*/g, ', ')
        : line
    )
    .join('\n');

/**
 * Read a markdown answer as speakable prose (ORA-464, "Read aloud").
 *
 * Speech synthesis reads what it is given literally, so an unstripped answer
 * comes out as "asterisk asterisk pneumonia asterisk asterisk" and a table is
 * read as a wall of pipes. Only the syntax is removed — the words, their order
 * and the line structure are left exactly as written, because this is the same
 * text the student is looking at.
 *
 * Code blocks are dropped rather than read: a block of code read aloud is
 * noise, and skipping it is what every reader does with one.
 */
export const markdownToSpeakableText = (
  text: string,
  options: { watchTokens?: boolean } = {}
): string => {
  // First, while its brackets are intact: in a course student's companion
  // (Courses V1) `[[watch 0:38-0:57]]` is said as "0:38 to 0:57" (ORA-1055),
  // not read through the link rule below.
  const withoutSyntax = (options.watchTokens ? watchTokensToText(text) : text)
    .replace(FENCED_CODE_RE, ' ')
    // Images first: an image is nothing to say, and its alt text is a label,
    // not prose.
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    // A link is read as its label, never as its URL.
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    // Inline code keeps its content: `HbA1c` is a word the student wants said.
    .replace(/`([^`]*)`/g, '$1')
    // Leading block syntax: headings, quotes, list bullets, numbered items.
    // The indent classes are `[ \t]`, never `\s`, so a pattern anchored to a
    // line start cannot reach back across the blank line above it and eat the
    // paragraph break — which is the one thing a listener hears.
    .replace(/^[ \t]{0,3}#{1,6}[ \t]+/gm, '')
    .replace(/^[ \t]{0,3}>[ \t]?/gm, '')
    .replace(/^[ \t]{0,3}[-*+][ \t]+/gm, '')
    .replace(/^[ \t]{0,3}\d+[.)][ \t]+/gm, '')
    // A horizontal rule is a pause, not a sentence.
    .replace(/^[ \t]{0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/gm, '')
    // Emphasis markers around a word, never a lone asterisk mid-sentence.
    .replace(/(\*\*\*|\*\*|\*)(?=\S)([\s\S]*?\S)\1/g, '$2')
    // Underscores are emphasis only at word boundaries — the same rule
    // CommonMark applies — so `lac_operon_model` and `snake_case` keep their
    // underscores instead of being run into one word.
    .replace(/(^|[^\w])(___|__|_)(?=\S)([\s\S]*?\S)\2(?!\w)/g, '$1$3')
    .replace(/~~(?=\S)([\s\S]*?\S)~~/g, '$1')
    .replace(/<[^>]*>/g, '');

  return decodeHtmlEntities(tablesToSentences(withoutSyntax))
    .replace(/[ \t]+/g, ' ')
    .replace(/^[ ,]+|[ ,]+$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};
